import { type DepartmentNode, MODULES } from '@erp/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Deadline, StatusBadge } from '@/components/task-bits';
import { useCan, useUser } from '@/lib/auth';
import { formatMoment, plural } from '@/lib/format';
import { departmentsTreeQuery, flattenTree, inboxQuery, notificationsQuery, tasksQuery } from '@/lib/queries';

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return 'Доброй ночи';
  if (h < 12) return 'Доброе утро';
  if (h < 18) return 'Добрый день';
  return 'Добрый вечер';
}

export function HomePage() {
  const user = useUser();
  const can = useCan();
  const tree = useQuery(departmentsTreeQuery);
  const notifications = useQuery(notificationsQuery);
  const firstName = user.employee?.fullName.split(' ')[1] ?? user.login;

  const flat = tree.data ? flattenTree(tree.data) : [];
  const headless = flat.filter((f) => !f.node.head && f.depth > 0).map((f) => f.node);
  const unread = (notifications.data ?? []).filter((n) => !n.readAt).slice(0, 5);
  const planned = MODULES.filter((m) => m.status === 'planned').sort((a, b) => a.sprint - b.sprint);

  return (
    <div className="max-w-[1100px]">
      <h1 className="text-[26px] font-semibold tracking-[-0.015em]">
        {greeting()}, {firstName}
      </h1>
      {user.employee && (
        <p className="mt-1 text-steel">
          {[user.employee.positionName, user.employee.departmentName].filter(Boolean).join(', ')}
        </p>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <div className="flex flex-col gap-6">
          {can('org.department.manage') && headless.length > 0 && (
            <HeadlessDepartments departments={headless} />
          )}

          <Approvals />

          <MyTasks />

          <section className="panel">
            <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
              <h2 className="font-semibold">Новые уведомления</h2>
              <Link to="/notifications" className="link text-[13px]">
                Все уведомления
              </Link>
            </div>
            {unread.length === 0 ? (
              <p className="px-5 py-6 text-steel">Непрочитанных нет.</p>
            ) : (
              <ul className="divide-y divide-line">
                {unread.map((n) => (
                  <li key={n.id} className="px-5 py-3">
                    <Link to={n.link ?? '/notifications'} className="block hover:text-link">
                      <span className="font-medium">{n.title}</span>
                      {n.body && <span className="text-steel"> — {n.body}</span>}
                    </Link>
                    <p className="mt-0.5 text-[12.5px] text-steel">{formatMoment(n.createdAt)}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <section className="panel self-start">
          <h2 className="border-b border-line px-5 py-3.5 font-semibold">Переезд с Битрикс24</h2>
          <p className="px-5 pt-4 text-steel">
            Структура, сотрудники, задачи, отпуска и заявки уже работают здесь. Остальные разделы подключаются
            по спринтам — до этого ими пользуемся в Битрикс24.
          </p>
          <ol className="px-5 py-4">
            {planned.map((m) => (
              <li key={m.code} className="flex items-baseline gap-3 py-1.5">
                <span className="w-14 shrink-0 text-[12.5px] tabular-nums text-steel">спринт {m.sprint}</span>
                <span>{m.title}</span>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}

function HeadlessDepartments({ departments }: { departments: DepartmentNode[] }) {
  return (
    <section className="panel border-l-[3px] border-l-signal">
      <div className="px-5 py-4">
        <h2 className="font-semibold">
          {departments.length} {plural(departments.length, ['отдел', 'отдела', 'отделов'])} без руководителя
        </h2>
        <p className="mt-1 text-steel">
          Заявки на отпуск от их сотрудников будут уходить сразу директору. Назначьте руководителей до запуска
          модуля отпусков.
        </p>
        <ul className="mt-3 flex flex-wrap gap-2">
          {departments.map((d) => (
            <li key={d.id}>
              <Link
                to="/org"
                search={{ department: d.id }}
                className="inline-block rounded-md border border-line-strong px-2.5 py-1 text-[13px] hover:border-graphite hover:bg-sunken"
              >
                {d.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Ближайшие по сроку задачи, которые на мне: просроченные — первыми. */
function MyTasks() {
  const q = useQuery(tasksQuery({ view: 'doing', order: 'deadline' }));
  const items = (q.data?.items ?? [])
    .filter((t) => t.status !== 'done' && t.status !== 'deferred')
    .slice(0, 6);
  return (
    <section className="panel">
      <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
        <h2 className="font-semibold">Мои задачи</h2>
        <Link to="/tasks" className="link text-[13px]">
          Все задачи
        </Link>
      </div>
      {q.isPending ? null : items.length === 0 ? (
        <p className="px-5 py-6 text-steel">Задач в работе нет.</p>
      ) : (
        <ul className="divide-y divide-line">
          {items.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-2.5">
              <Link to="/tasks/$id" params={{ id: t.id }} className="min-w-0 flex-1 truncate hover:text-link">
                {t.title}
              </Link>
              <StatusBadge status={t.status} />
              <Deadline iso={t.deadline} overdue={t.isOverdue} compact />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Задания по маршрутам, которые ждут меня. Блок виден, только когда они есть. */
function Approvals() {
  const q = useQuery(inboxQuery('open'));
  const items = q.data ?? [];
  if (!items.length) return null;
  return (
    <section className="panel border-l-[3px] border-l-signal">
      <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
        <h2 className="font-semibold">
          {items.length} {plural(items.length, ['задание ждёт', 'задания ждут', 'заданий ждут'])} вашего
          решения
        </h2>
        <Link to="/approvals" className="link text-[13px]">
          Согласования
        </Link>
      </div>
      <ul className="divide-y divide-line">
        {items.slice(0, 5).map((i) => (
          <li key={i.taskId}>
            <Link to={i.link} className="flex flex-wrap items-baseline gap-x-3 px-5 py-2.5 hover:bg-paper/70">
              <span className="min-w-0 flex-1 truncate">
                <span className="text-steel">{i.routeTitle}:</span> {i.entityTitle}
              </span>
              <span className="text-[13px] font-medium">{i.title}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
